import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border: none;
  text-align: left;
  cursor: pointer;
  font-weight: 600;
  color: var(--primary);
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  svg {
    transition: transform 0.3s ease;
    ${props => props.isOpen && 'transform: rotate(180deg);'}
  }
`;

const Content = styled.div`
  max-height: ${props => (props.isOpen ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.5s ease;
`;

const GuidelinesContent = styled.div`
  background-color: #fff3f5;
  padding: 1.5rem;
  border-left: 4px solid var(--primary);
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  color: var(--dark);
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const CollapsibleSafetyGuidelines = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Container>
      <ToggleButton onClick={toggleOpen} isOpen={isOpen}>
        <div className="d-flex align-items-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-shield-check me-2" viewBox="0 0 16 16">
            <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z"/>
            <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
          </svg>
          Safety Guidelines
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
        </svg>
      </ToggleButton>
      
      <Content isOpen={isOpen}>
        <GuidelinesContent>
          <Section>
            <SectionTitle>Safe Implementation Guidelines</SectionTitle>
            <List>
              <li><strong>Start gradually</strong> - Begin with small changes and observe your child's response</li>
              <li><strong>Be consistent</strong> - Maintain a regular routine while being responsive to your child's needs</li>
              <li><strong>Follow safe sleep guidelines</strong> - Ensure your child's sleep environment follows Red Nose Australia recommendations</li>
              <li><strong>Monitor your child</strong> - Regularly check on your child during sleep training</li>
              <li><strong>Trust your instincts</strong> - If something doesn't feel right, pause and reassess</li>
              <li><strong>Adjust as needed</strong> - Be prepared to modify the plan based on your child's individual response</li>
              <li><strong>Take breaks</strong> - If you or your child become too stressed, take a break and try again later</li>
            </List>
          </Section><br />

          <Section>
            <SectionTitle>When to Contact a Healthcare Professional</SectionTitle>
            <p>Stop implementing the sleep plan and seek medical advice if your child:</p>
            <List>
              <li>Has difficulty breathing or changes in breathing patterns</li>
              <li>Shows signs of illness (fever, vomiting, unusual crying)</li>
              <li>Appears excessively lethargic or difficult to wake</li>
              <li>Shows signs of dehydration (dry mouth, no tears when crying, decreased wet nappies)</li>
              <li>Experiences any concerning changes in behavior or health</li>
              <li>Is inconsolable for an extended period</li>
              <li>Shows signs of physical distress during sleep training</li>
            </List>
          </Section>
        </GuidelinesContent>
      </Content>
    </Container>
  );
};

export default CollapsibleSafetyGuidelines;
